import { Skeleton } from "@mantine/core";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { Zap } from "lucide-react";
import { type ReactNode, useState } from "react";

import {
  getListTemplatesQueryKey,
  listTemplates,
  useListTemplateCategories,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  ListTemplatesParams,
  TemplateCard,
  TemplateCategory,
  TemplateEstimate,
  TemplatePage,
} from "../../../api/generated/model";
import { freshUrls, soonestExpiry } from "../../../api/signed-urls";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { formatCount } from "../../../ui/format";
import { LoadAhead, morePages } from "../../../ui/LoadAhead";
import { SubmitSearch } from "../../../ui/SubmitSearch";
import { TemplateCover } from "../../../ui/TemplateCover";
import page from "../actors/actors.module.css";
import create from "../create/create.module.css";
import gen from "../generations/generations.module.css";
import home from "../pro-home.module.css";
import classes from "./templates.module.css";

/** The Templates page's own read: 24 a page, its key, so the two share it. */
const PAGE = 24;
const SEARCH_MAX = 100;

type Pages = InfiniteData<TemplatePage, string | undefined>;

/** A run's credits as the prototype says them: "about 120", or a range. */
function aboutOf(estimate: TemplateEstimate): string {
  const { credits_min: least, credits_max: most } = estimate;
  return least === most
    ? `about ${formatCount(least)}`
    : `${formatCount(least)} to ${formatCount(most)}`;
}

function Card({ template, teamId, brandId, onOpen }: {
  template: TemplateCard;
  teamId: string;
  brandId: string;
  onOpen: ((template: TemplateCard) => void) | undefined;
}) {
  const kind = template.media === "video" ? "Video template" : "Image template";
  const body = (
    <>
      {/* Its preview plays, as Classic's card does (spec ui-perf/template-card-previews). */}
      <span className={home.thumb}>
        <TemplateCover template={template} className={home.thumbCover} iconClassName={home.thumbIcon} />
        <span className={classes.kind}>{template.coming_soon ? "Coming soon" : kind}</span>
      </span>
      <b className={home.templateTitle}>{template.title}</b>
      {template.description === null ? null : (
        <span className={home.tiny}>{template.description}</span>
      )}
      <span className={classes.facts}>
        {template.category}
        {template.estimate === null || template.coming_soon ? null : (
          <>
            {" · "}
            <Zap size={12} aria-hidden /> {aboutOf(template.estimate)}
          </>
        )}
      </span>
    </>
  );
  // A coming soon card opens nothing, as the Templates page's.
  if (template.coming_soon) {
    return (
      <div className={home.templateCard} aria-disabled="true">
        {body}
      </div>
    );
  }
  // In Pro the preview opens here (spec ui-perf/ui-switch-classic-simple-pro, PR e2).
  if (onOpen !== undefined) {
    return (
      <button
        type="button"
        className={`${home.templateCard} ${classes.open}`}
        aria-label={template.title}
        onClick={() => onOpen(template)}
      >
        {body}
      </button>
    );
  }
  return (
    // The template's preview, Use and form are the Templates page's own (decision 28).
    <Link
      to="/teams/$teamId/brands/$brandId/templates"
      params={{ teamId, brandId }}
      search={{ template: template.id }}
      className={home.templateCard}
      aria-label={template.title}
    >
      {body}
    </Link>
  );
}

/**
 * The prototype's `#templates`: the Templates page's own cards (its search, categories and order,
 * 24 a page), drawn as the prototype draws them; a card opens the template's preview on the
 * Templates page (spec ui-perf/pro-studio-redesign, PR d2, decision 28).
 */
export function TemplatesPage({ onOpen, generations, panel }: {
  /** A card opens the template's preview here, not on the Templates page. */
  onOpen?: ((template: TemplateCard) => void) | undefined;
  /** The runs of the brand's templates: with it, the page has the prototype's two tabs. */
  generations?: ReactNode;
  /** A template's form, at the page's foot while it is open. */
  panel?: ReactNode;
} = {}) {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/studio/pro/templates",
  });
  const tab = useSearch({ from: "/signed-in/teams/$teamId/brands/$brandId/studio/pro/templates" }).tab;
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<TemplateCategory | null>(null);
  const categories = useListTemplateCategories(teamId);
  const params: ListTemplatesParams = {
    ...(search === "" ? {} : { search }),
    ...(category === null ? {} : { category }),
  };
  const templates = useInfiniteQuery({
    queryKey: [...getListTemplatesQueryKey(teamId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listTemplates(
        teamId,
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    ...freshUrls<Pages>((data) => soonestExpiry(data.pages.map((each) => each.urls_expire_at))),
  });
  const items = templates.data?.pages.flatMap((each) => each.items);
  const failed = templates.error ?? categories.error;
  const chips: { label: string; value: TemplateCategory | null }[] = [
    { label: "All", value: null },
    ...(categories.data?.categories ?? []).map((each) => ({ label: each, value: each })),
  ];
  return (
    <div className={create.page}>
      <div className={page.head}>
        <div className={page.titles}>
          <h1 className={page.title}>Templates</h1>
          <p className={page.lede}>
            Proven ad formats. Fill in a few inputs and the template does the steps.
          </p>
        </div>
      </div>
      {generations === undefined ? null : (
        <nav className={page.tabs} aria-label="Templates tabs">
          {(
            [
              { label: "Library", tab: undefined },
              { label: "My generations", tab: "my-generations" },
            ] as const
          ).map((each) => (
            <Link
              key={each.label}
              to="/teams/$teamId/brands/$brandId/pro/templates"
              params={{ teamId, brandId }}
              search={{ tab: each.tab }}
              className={page.tab}
              aria-current={each.tab === tab ? "page" : undefined}
              activeOptions={{ exact: true, includeSearch: true }}
            >
              {each.label}
            </Link>
          ))}
        </nav>
      )}
      {generations !== undefined && tab === "my-generations" ? (
        generations
      ) : (
        <>
      <div className={gen.toolbar}>
        <SubmitSearch
          label="Search templates"
          value={search}
          onSearch={setSearch}
          maxLength={SEARCH_MAX}
          className={classes.search}
        />
        <div className={gen.kinds} role="toolbar" aria-label="Categories">
          {chips.map((each) => (
            <button
              key={each.label}
              type="button"
              className={gen.kind}
              aria-pressed={each.value === category}
              onClick={() => setCategory(each.value)}
            >
              {each.label}
            </button>
          ))}
        </div>
      </div>
      {failed === null ? null : <ErrorAlert error={failed} />}
      {items === undefined ? (
        <div className={classes.grid}>
          {[0, 1, 2, 3].map((each) => (
            <Skeleton key={each} className={home.templateSkeleton} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className={create.empty}>
          <b>No templates found</b>
          <span className={home.small}>Try a different search or category.</span>
        </div>
      ) : (
        <ul className={classes.grid} aria-label="Templates">
          {items.map((template) => (
            <li key={template.id}>
              <Card template={template} teamId={teamId} brandId={brandId} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      )}
      {/* The next page as the end shows, as the Templates page reads it. */}
      <LoadAhead more={morePages(templates)} loaded={items?.length ?? 0} label="Loading more templates" />
        </>
      )}
      {panel === undefined || panel === null ? null : <div className={classes.panel}>{panel}</div>}
    </div>
  );
}
